import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';

export default function PrivacyPolicyPage() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        // Try API first, fallback to local JSON
        const response = await fetch(`${API_URL}/api/privacy`);
        if (response.ok) {
          const data = await response.json();
          setPolicy(data);
        } else {
          throw new Error('API failed');
        }
      } catch (err) {
        // Fallback to local JSON
        try {
          const response = await fetch(`${process.env.PUBLIC_URL}/cms_data/privacy.json`);
          const data = await response.json();
          setPolicy(data);
        } catch (fallbackErr) {
          console.error('Failed to load privacy policy:', fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPolicy();
  }, []);

  // Parse markdown-style bold text
  const parseContent = (content) => {
    if (!content) return null;
    
    const parts = content.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-brand-dark">{part.slice(2, -2)}</strong>;
      }
      // Handle newlines
      return part.split('\n').map((line, j) => (
        <React.Fragment key={`${i}-${j}`}>
          {j > 0 && <br />}
          {line}
        </React.Fragment>
      ));
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-dark via-[#0a0875] to-brand-dark py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-primary/20 rounded-2xl mb-6">
              <Shield className="w-8 h-8 text-brand-primary" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-white/70 text-lg">
              How we protect and handle your personal information
            </p>
            {policy?.lastUpdated && (
              <p className="text-white/50 text-sm mt-4">
                Last updated: {new Date(policy.lastUpdated).toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-8 sm:p-12 space-y-10">
              {policy?.sections?.map((section, index) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: index * 0.1 }}
                  className={index > 0 ? "pt-10 border-t border-gray-100" : ""}
                >
                  <h2 className="font-display text-2xl font-bold text-brand-dark mb-4">
                    {section.title}
                  </h2>
                  <div className="text-gray-600 leading-relaxed space-y-4">
                    {parseContent(section.content)}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Contact Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 bg-brand-primary/5 border border-brand-primary/20 rounded-2xl p-8 text-center"
          >
            <Mail className="w-10 h-10 text-brand-primary mx-auto mb-4" />
            <h3 className="font-display text-xl font-bold text-brand-dark mb-2">
              Questions about your data?
            </h3>
            <p className="text-gray-600 mb-4">
              Contact us at any time to request information about your personal data.
            </p>
            <a 
              href="mailto:info@mt-productions.co.uk"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-full font-semibold hover:bg-brand-dark transition-colors"
            >
              <Mail className="w-5 h-5" />
              info@mt-productions.co.uk
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
